import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { styled } from "@mui/material/styles";
import ExitToApp from "@mui/icons-material/ExitToApp";
import Sync from "@mui/icons-material/Sync";

import BadgeCount from "../../components/badgeCount/BadgeCount";

import { withState } from "../../../services/state";
import sidebarNavigation from "../../../services/navbar";

// Styled components
const PageSidebarRoot = styled("div")(({ theme }) => ({
  height: "100%",
  width: 300,
  backgroundColor: "#393A39",
  color: "#fff",
  overflowY: "auto",
  [theme.breakpoints.down("sm")]: {
    zIndex: 1000,
    position: "absolute",
    width: 270,
  },
}));

const CurrentView = styled("div")({
  padding: 20,
});

const CurrentlySelected = styled("div")({
  margin: "10px 0",
  cursor: "pointer",
  minHeight: "40px",
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
});

const ClubLogo = styled("img")({
  height: 50,
});

const SelectItem = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  margin: "10px 0",
  cursor: "pointer",
  minHeight: "40px",
  opacity: 0.4,
});

const NavigationItem = styled("div")(({ active }) => ({
  borderBottom: "1px solid #515151",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "13px 10px",
  borderLeft: active ? "3px solid #00823E" : "3px solid #393A39",
  cursor: "pointer",
  color: "#fff",
  opacity: active ? 1 : 0.5,
}));

const NavigationItemTitle = styled("div")({
  paddingBottom: 3,
  marginLeft: 5,
});

const NavLink = styled(Link)({
  textDecoration: "none",
});

const SidebarNavigationItem = ({ context, navigationItem }) => {
  const isActive = window.location.pathname === navigationItem.route;
  const notifications = context.currentPlayer?.notifications || {};

  // Map badge keys to actual counts
  const unreadMessageCount = notifications[navigationItem.badgeKeys?.[0]] || 0;
  const unpaidFeesCount =
    notifications.unpaidEvents &&
    navigationItem.badgeKeys?.includes("unpaidEvents")
      ? notifications.unpaidEvents
      : 0;
  const unRespondedRequestsCount =
    notifications.unrespondedAvailabilityRequests &&
    navigationItem.badgeKeys?.includes("unrespondedAvailabilityRequests")
      ? notifications.unrespondedAvailabilityRequests
      : 0;

  const renderItem = (
    <NavigationItem active={isActive}>
      <div>{navigationItem.icon}</div>
      <NavigationItemTitle>
        {navigationItem.title.replace(
          "{{AdminType}}",
          context.currentClub.isTreasurer ? "Admin" : "Captain",
        )}
      </NavigationItemTitle>
      <BadgeCount
        unreadMessageCount={unreadMessageCount}
        unpaidFeesCount={unpaidFeesCount}
        unRespondedRequestsCount={unRespondedRequestsCount}
      />
    </NavigationItem>
  );

  if (navigationItem.href) {
    return (
      <a href={navigationItem.href} style={{ textDecoration: "none" }}>
        {renderItem}
      </a>
    );
  }

  return <NavLink to={navigationItem.route}>{renderItem}</NavLink>;
};

// Render navigation list filtered by user roles
const renderNavigation = (context) =>
  sidebarNavigation
    .filter((item) => {
      return (
        !item.treasurerOrCaptainOnly ||
        context.currentClub.isTreasurer ||
        context.currentClub.isCaptain
      );
    })
    .map((item) => (
      <SidebarNavigationItem
        key={item.title}
        navigationItem={item}
        context={context}
      />
    ));

// Main sidebar component
function PageSidebar({ context, dispatch, showMinimizedSidebar }) {
  const navigate = useNavigate();

  const currentPlayerName =
    context.currentPlayer?.firstName && context.currentPlayer?.lastName
      ? `${context.currentPlayer.firstName} ${context.currentPlayer.lastName}`
      : null;
  const currentClubName = context.currentClub?.name;

  const showSelectPlayer = () => {
    dispatch({ type: "showSelectPlayer" });
  };

  const logout = () => {
    dispatch({ type: "clearToken" });
    dispatch({ type: "clearSessionVariables" });
    navigate("/");
  };

  // Listen for player notification refresh event
  React.useEffect(() => {
    const handleRefreshNotifications = async () => {
      try {
        const playerData = await (
          await fetch(
            `/api/players/${context.currentPlayer.id}`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${context.tokens?.accessToken}`,
              },
            }
          )
        ).json();
        
        if (playerData && !playerData.reason) {
          dispatch({
            type: "storeCurrentPlayer",
            currentPlayer: playerData,
          });
        }
      } catch (e) {
        console.log("Error refreshing player notifications:", e);
      }
    };

    window.addEventListener('refreshPlayerNotifications', handleRefreshNotifications);
    
    return () => {
      window.removeEventListener('refreshPlayerNotifications', handleRefreshNotifications);
    };
  }, [context.currentPlayer.id, context.tokens?.accessToken, dispatch]);

  // Hide sidebar on small screens if minimized
  if (window.innerWidth < 960 && !showMinimizedSidebar) return <div />;

  return (
    <PageSidebarRoot>
      <CurrentView>
        {currentPlayerName ? (
          <CurrentlySelected onClick={showSelectPlayer}>
            <ClubLogo alt="Club logo" src={context.currentClub.logoUrl} />
            <div>
              <div>{currentPlayerName}</div>
              <div>{currentClubName}</div>
            </div>
          </CurrentlySelected>
        ) : (
          <SelectItem>Select a player</SelectItem>
        )}

        {context.profile?.players?.length > 0 && (
          <SelectItem onClick={showSelectPlayer}>
            <Sync /> Switch accounts
          </SelectItem>
        )}
      </CurrentView>

      {renderNavigation(context)}

      <NavigationItem onClick={logout}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <ExitToApp />
          <NavigationItemTitle>Logout</NavigationItemTitle>
        </div>
      </NavigationItem>
    </PageSidebarRoot>
  );
}

export default withState(PageSidebar);
